import {
  Grid,
  HStack,
  Input,
  Link,
  Radio,
  RadioGroup,
  Stack,
  Text,
  Box
} from '@chakra-ui/react'
import { useFormContext, useController } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'

import { CompanyTaxInvoiceForm } from './CompanyTaxInvoiceForm'
import { FormControl } from '@/components/FormControl'

import { FreeTrialFormInput } from '../types'
import { NDATA_SALES_EMAIL, QUOTATION_URL, PHONE_HREF } from '@/constants/links'

import { TAX } from '@/features/free-trial/components/CompanyTaxInvoiceForm/constants'

export const AddressForm = (): React.ReactElement => {
  const {
    control,
    formState: { errors },
    register,
    watch,
  } = useFormContext<FreeTrialFormInput>()
  const { t } = useTranslation(['freeTrial'])

  const { field } = useController({ control, name: 'taxpayer_type', defaultValue: 'individual' })

  const taxpayerType = watch('taxpayer_type', 'individual')

  return (
    <Stack spacing={4}>
      <Stack spacing={2}>
        <Text>{t('form.billingInformation')}</Text>
        <Text sx={{ fontSize: 'xs' }}>{t('form.taxInvoice')}</Text>
      </Stack>

      <Grid sx={{ gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
        <RadioGroup
          colorScheme="brand"
          sx={{
            gridColumn: '1 / -1',
          }}
          {...field}
        >
          <HStack>
            <Radio value="individual">{t('form.individual')}</Radio>
            <Radio value="company">{t('form.company')}</Radio>
          </HStack>
        </RadioGroup>

        {taxpayerType === 'company' ? (
          <Box sx={{ gridColumn: '1 / -1' }}>
            <CompanyTaxInvoiceForm />
          </Box>
        ) : (
          <Grid
            sx={{
              gridTemplateColumns: '1fr 1fr',
              gap: '8px',
              gridColumn: '1 / -1'
            }}
          >
            <Input
              isRequired
              placeholder={t('form.namePlaceholder')}
              size="lg"
              {...register('name')}
            />
            <FormControl errorMsg={errors.tax_id?.message}>
              <Input
                isRequired
                maxLength={TAX.THAI_TAX_ID_LENGTH}
                minLength={TAX.THAI_TAX_ID_LENGTH}
                size="lg"
                {...register('tax_id')}
                placeholder={t('form.taxIdPlaceholder')}
              />
            </FormControl>
            <Input
              isRequired
              placeholder={t('form.addressPlaceholder')}
              size="lg"
              sx={{ gridColumn: '1/-1' }}
              {...register('address')}
            />
          </Grid>
        )}

        <Text
          textStyle="formHelper"
          sx={{
            gridColumn: '1/-1',
            a: {
              color: 'brand.400',
            },
          }}
        >
          <Trans
              t={t}
              i18nKey="form.contactUsAt"
              values={{
                url: QUOTATION_URL,
                mail: NDATA_SALES_EMAIL,
                phone: PHONE_HREF,
              }}
              components={{
                quotationLink: (
                  <Link
                    href={QUOTATION_URL}
                    isExternal
                    sx={{ color: 'brand.400', textDecor: 'underline' }}
                  />
                ),
                emailLink: (
                  <Link
                    href={`mailto:${NDATA_SALES_EMAIL}`}
                    sx={{ color: 'brand.400', textDecor: 'underline' }}
                  />
                ),
                phoneLink: (
                  <Link
                    href={`tel:${PHONE_HREF}`}
                    sx={{ color: 'brand.400', textDecor: 'underline' }}
                  />
                ),
              }}
            />
        </Text>
      </Grid>
    </Stack>
  )
}
